import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ClipboardList, FileText, GraduationCap, LayoutDashboard, Package, RotateCcw, Search, Store, User, X,
} from 'lucide-react';
import type { MenuItem } from '../../stores/menuStore';
import type { MenuIcon } from '../../navigation/menuIcon';
import type { MenuRules } from '../../navigation/menuTree';
import { thingDestination } from '../../navigation/searchDestinations';
import { WORDS, greyLine, layoutSearch, type SearchRow } from '../../navigation/searchGroups';
import { buildSearchIndex, entriesForPaths, searchPages } from '../../navigation/searchIndex';
import type { ThingKind, ThingRow } from '../../services/searchApi';
import { topVisitedPaths } from '../../utils/pageVisits';
import { SEARCH_PROMPT } from './openSearch';
import { MAX_SEARCH_CHARS, useSearchResults } from './useSearchResults';

interface PageSearchProps {
  onClose: () => void;
  /** The menu as the shell holds it. Search lists only what these rows allow. */
  items: MenuItem[];
  /** The shell's hiding rules. Their project is the one people and stores are searched in. */
  rules: MenuRules;
  /** Whose "pages you open most" to show before anything is typed. */
  userKey: string | null | undefined;
}

const LIST_ID = 'page-search-results';
const MOST_OPENED_SHOWN = 6;

const KIND_ICON: Record<ThingKind, MenuIcon> = {
  person: User,
  store: Store,
  task: ClipboardList,
  training: GraduationCap,
  product: Package,
  dashboard: LayoutDashboard,
  report: FileText,
};

// A small quiet label over each group, with a little space above it.
const HEADING = 'px-3 pb-1 pt-3 text-xs font-medium text-gray-500';
// Every line of words that is not a row starts where the rows' icons do.
const LEAD = 'px-3 py-1.5 text-sm text-gray-500';
const STATUS_LINE = 'px-3 py-3 text-sm text-gray-500';
// A row: rounded, and set in from the panel's edge so its highlight is a soft block.
const ROW = 'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2';
const ICON_SQUARE = 'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg';

/**
 * A row's icon in its small square: one tint for every kind of row. On the highlighted
 * row the square turns white, so it still shows on the row's own tint. `quiet` is for
 * the row that is not a result: its icon keeps the square's place and has no tint.
 */
function RowIcon({ icon: Icon, isActive, quiet = false }: { icon: MenuIcon; isActive: boolean; quiet?: boolean }): ReactElement {
  const tone = quiet
    ? (isActive ? 'text-primary-600' : 'text-gray-400')
    : `${isActive ? 'bg-white' : 'bg-primary-50'} text-primary-600`;
  // A div: the spans of a row are its words.
  return (
    <div className={`${ICON_SQUARE} ${tone}`}>
      <Icon className="h-4 w-4" aria-hidden="true" />
    </div>
  );
}

function RowBody({ row, isActive }: { row: SearchRow; isActive: boolean }): ReactElement {
  if (row.type === 'page') {
    const { entry } = row;
    return (
      <>
        <RowIcon icon={entry.icon} isActive={isActive} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-gray-900">{entry.name}</span>
          {entry.line && <span className="block truncate text-xs text-gray-500">{entry.line}</span>}
        </span>
        <span className="max-w-[40%] flex-shrink-0 truncate text-xs text-gray-400">{entry.where}</span>
      </>
    );
  }
  if (row.type === 'allPages') {
    // Quieter than a result: grey and unweighted, because it is not one.
    return (
      <>
        <RowIcon icon={ChevronDown} isActive={isActive} quiet />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-gray-600">{WORDS.allPages(row.count)}</span>
        </span>
      </>
    );
  }
  if (row.type === 'retry') {
    return (
      <>
        <RowIcon icon={RotateCcw} isActive={isActive} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-gray-900">{WORDS.tryAgain}</span>
        </span>
      </>
    );
  }
  const grey = greyLine(row.thing);
  return (
    <>
      <RowIcon icon={KIND_ICON[row.kind]} isActive={isActive} />
      {/* One line, never wrapped: the name, then its detail in grey until the row ends. */}
      <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
        <span className="text-sm font-medium text-gray-900">{row.thing.name}</span>
        {grey && <span className="ml-2">{grey}</span>}
      </span>
    </>
  );
}

/**
 * Find a person, a store, a page or anything else the system holds by typing a few
 * letters.
 *
 * Pages, dashboards, their lists and terms are found in the browser, from the person's
 * own menu, and show at once. From three letters, with a project selected, the server
 * is asked too (useSearchResults) and what it finds is added below in groups: people,
 * stores, tasks, training, products, and the dashboards and reports the team built.
 * Rows already on screen never move when it answers (navigation/searchGroups). While
 * the server is asked, the pages take four rows and "All N pages" opens the rest, so
 * what it finds is on screen without scrolling.
 *
 * Picking a row closes the pop-up and opens it. A person or a store opens "Where do you
 * want to look?" (pages/SearchResultPage); everything else opens its own page.
 *
 * Mounted only while it is open, so every opening starts with an empty box. The shell
 * owns the Search button and the "/" key, and on close puts focus back where it was
 * (the Search button, or wherever the person was when they pressed "/").
 * Results are a listbox driven from the text box (arrow keys, Enter), so focus never
 * has to leave the box.
 */
export default function PageSearch({ onClose, items, rules, userKey }: PageSearchProps): ReactElement {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  // "All N pages" was opened, for the text now in the box.
  const [allPages, setAllPages] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const index = useMemo(() => buildSearchIndex(items, rules), [items, rules]);
  // Asked for more than are shown: an address the person no longer holds is dropped.
  const mostOpened = useMemo(
    () => entriesForPaths(index, topVisitedPaths(userKey, MOST_OPENED_SHOWN * 2)).slice(0, MOST_OPENED_SHOWN),
    [index, userKey],
  );

  const typed = query.trim();
  const pageRows = useMemo(() => (typed ? searchPages(index, typed) : mostOpened), [typed, index, mostOpened]);

  // No project yet is null, whatever an older saved sign-in left in its place.
  const { server, retry } = useSearchResults(typed, rules.selectedProjectId || null);
  const destination = useCallback(
    (kind: ThingKind, thing: ThingRow) => thingDestination(kind, thing.id, (path) => entriesForPaths(index, [path]).length > 0, thing.name),
    [index],
  );
  const { blocks, rows, line } = useMemo(
    () => layoutSearch({ typed, pageRows, allPages, server, destination }),
    [typed, pageRows, allPages, server, destination],
  );

  useEffect(() => {
    inputRef.current?.focus();
    // The page behind must not scroll while the dialog is up.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // On the document, not the dialog: after a click on the dimmed page, focus is outside it.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Keep the highlighted result in view when the arrow keys run past the visible rows.
  // Only for the keys: scrolling a row under a resting mouse would highlight the next
  // row, which would scroll again.
  const movedByKeys = useRef(false);
  useEffect(() => {
    if (!movedByKeys.current) return;
    movedByKeys.current = false;
    const row = listRef.current?.querySelector('[aria-selected="true"]');
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const openRow = (row: SearchRow) => {
    // Shows the rest of the pages in place. The highlight keeps its position, which is
    // now the first page this adds.
    if (row.type === 'allPages') {
      setAllPages(true);
      return;
    }
    // Asks again in place: the pop-up stays open and the box keeps its text.
    if (row.type === 'retry') {
      retry();
      // Back to the first row. Left at this row's position, the highlight would be on
      // whatever the answer puts there, and a second Enter would open it.
      setActive(0);
      return;
    }
    onClose();
    // Pushed, never replaced: a page that fills its search box from a link reads only an
    // address the person went to (hooks/useLinkedSearch).
    navigate(row.type === 'page' ? row.entry.to : row.to);
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      movedByKeys.current = true;
      setActive((i) => Math.min(i + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      movedByKeys.current = true;
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      const row = rows[active];
      if (row) {
        e.preventDefault();
        openRow(row);
      }
    }
  };

  // Two stops only, the text box and Close: Tab moves between them and never leaves the dialog.
  const onDialogKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    (document.activeElement === inputRef.current ? closeRef : inputRef).current?.focus();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-16 sm:pt-[12vh]">
      {/* A light dim and a blur: the page stays visible behind the glass, out of focus. */}
      <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onKeyDown={onDialogKeyDown}
        // The top bar's glass (index.css): frosted white with its own shadow and hairline ring.
        // Whiter than the small glass panels: a list is read here, over a dimmed page.
        className="glass-panel relative w-full max-w-xl overflow-hidden rounded-2xl bg-white/90"
      >
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
          {/* As wide as a row's icon square, so the icon sits over the rows' icons and
              what is typed starts where their names do. */}
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center" aria-hidden="true">
            <Search className="h-5 w-5 text-primary-500" />
          </span>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={SEARCH_PROMPT}
            aria-autocomplete="list"
            aria-expanded={rows.length > 0}
            aria-controls={LIST_ID}
            aria-activedescendant={rows[active] ? `${LIST_ID}-${active}` : undefined}
            autoComplete="off"
            spellCheck={false}
            // As long as the server takes, and longer than any page name or term; a pasted
            // block of text is cut here.
            maxLength={MAX_SEARCH_CHARS}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
              // Every page was asked for under one text, not the next. A space after it
              // is the same text, and folding the list for it would move rows for nothing.
              if (e.target.value.trim() !== typed) setAllPages(false);
            }}
            onKeyDown={onInputKeyDown}
            placeholder={SEARCH_PROMPT}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="-mr-1.5 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {(blocks.length > 0 || line) && (
          <div className="max-h-[60vh] overflow-y-auto px-2 pb-2 pt-1">
            {blocks.length > 0 && (
              <div ref={listRef} id={LIST_ID} role="listbox" aria-label={typed ? 'Results' : WORDS.mostOpened}>
                {blocks.map((block) => (
                  <div key={block.key} role="group" aria-label={block.heading ?? block.lead ?? undefined}>
                    {block.heading && <p aria-hidden="true" className={HEADING}>{block.heading}</p>}
                    {block.lead && <p className={LEAD}>{block.lead}</p>}
                    {block.rows.map(({ row, index: i }) => {
                      const isActive = i === active;
                      return (
                        <div
                          key={row.key}
                          id={`${LIST_ID}-${i}`}
                          role="option"
                          aria-selected={isActive}
                          onMouseMove={() => setActive(i)}
                          // Pressing a row must not take focus out of the box: two rows
                          // leave the pop-up open, and typing and the arrow keys would
                          // then do nothing until the box was clicked again.
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => openRow(row)}
                          className={`${ROW} ${isActive ? 'bg-primary-50' : ''}`}
                        >
                          <RowBody row={row} isActive={isActive} />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
            {line && <p role="status" className={STATUS_LINE}>{line}</p>}
          </div>
        )}

        <p className="hidden items-center gap-4 border-t border-gray-100 px-5 py-2.5 text-xs text-gray-400 sm:flex">
          <span>↑ ↓ to move</span>
          <span>Enter to open</span>
          <span>Esc to close</span>
        </p>
      </div>
    </div>,
    document.body,
  );
}
